import Box, { BoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import MuiTab from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import { ElementType, PropsWithChildren, ReactNode, useEffect, useState } from 'react';

const StyledWrapper = styled(Box)`
    flex-flow: nowrap;
    white-space: pre-wrap;
`;

type ITabPanelProps = PropsWithChildren<
    BoxProps & {
        index: number;
        value: number;
        hidingMethod: 'css' | 'js';
    }
>;
export type ITabsProps<ContentContainer extends ElementType, TabContainer extends ElementType> = {
    onChange?: (id: string) => void;
    contentContainer?: ContentContainer;
    tabsContainer?: TabContainer;
    hidingMethod?: 'css' | 'js';
    tabs: {
        id: string;
        display?: boolean;
        label: ReactNode;
        content: ReactNode;
    }[];
};

const TabPanel = ({ hidingMethod = 'js', children, value, index, ...rest }: ITabPanelProps) => (
    <Box
        {...rest}
        className="tabContent"
        hidden={value !== index}
        sx={{
            ...rest.sx,
            display: hidingMethod === 'js' || index === value ? 'block' : 'none'
        }}
    >
        {hidingMethod === 'css' || value === index ? children : null}
    </Box>
);

export default function Tabs<ContentContainer extends ElementType, TabContainer extends ElementType>({
    hidingMethod = 'js',
    tabs,
    tabsContainer,
    contentContainer,
    onChange
}: ITabsProps<ContentContainer, TabContainer>) {
    const [value, setValue] = useState(0);

    useEffect(() => {
        if (onChange) {
            onChange(tabs[value].id);
        }
    }, [tabs, value, onChange]);

    return (
        <>
            <StyledWrapper {...(tabsContainer ? { component: tabsContainer } : {})} sx={{ marginBottom: 2 }}>
                <MuiTabs
                    data-testid="tabButtons"
                    variant="scrollable"
                    className="tabsContainer"
                    value={value}
                    onChange={(e, index) => {
                        console.log(e);
                        console.log(e.target);
                        setValue(index);
                    }}
                >
                    {tabs.map((tab) => (
                        <MuiTab
                            className="tab"
                            key={tab.id}
                            label={tab.label}
                            wrapped={false}
                            data-testid={`tab-button${tab.id}`}
                        />
                    ))}
                </MuiTabs>
            </StyledWrapper>
            <Box {...(contentContainer ? { component: contentContainer } : {})}>
                {tabs.map((tab, index) => (
                    <TabPanel
                        key={index}
                        hidingMethod={hidingMethod ?? 'js'}
                        index={index}
                        value={value}
                        data-testid={`tab-content-${tab.id}`}
                        sx={{ width: '100%' }}
                    >
                        {tab.content}
                    </TabPanel>
                ))}
            </Box>
        </>
    );
}
